// Shadow Network — Notifications page (full list grouped by date).
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import { UserAvatar } from "../components/shadow/UserAvatar";

import API from "../lib/api";
const COPY = {
  NEW_FOLLOWER: "notifNewFollower",
  LIKE_ANALYSIS: "notifLikeAnalysis",
  COMMENT_ANALYSIS: "notifCommentAnalysis",
  MENTION: "notifMention",
  BADGE_UNLOCKED: "notifBadgeUnlocked",
  NEW_ANALYSIS_FROM_FOLLOWING: "notifNewAnalysisFromFollowing",
  RANKING_CHANGE: "notifRankingChange",
};

const groupByDate = (items, lang) => {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const labels = {
    today: { es: "Hoy", en: "Today", sk: "Dnes", de: "Heute", "zh-CN": "今天" }[lang] || "Hoy",
    yesterday: { es: "Ayer", en: "Yesterday", sk: "Včera", de: "Gestern", "zh-CN": "昨天" }[lang] || "Ayer",
    earlier: { es: "Anterior", en: "Earlier", sk: "Skôr", de: "Zurück", "zh-CN": "上一个" }[lang] || "Anterior",
  };
  const groups = { today: [], yesterday: [], earlier: [] };
  for (const n of items) {
    const d = new Date(n.created_at).toDateString();
    if (d === today) groups.today.push(n);
    else if (d === yesterday) groups.yesterday.push(n);
    else groups.earlier.push(n);
  }
  return { groups, labels };
};

const NotificationsPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shadow/social/notifications?limit=100`, {
        withCredentials: true,
      });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markAllRead = async () => {
    await axios.patch(
      `${API}/shadow/social/notifications/read-all`,
      {},
      { withCredentials: true }
    );
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-white/65 mb-4">Acceso de operador requerido.</p>
          <Link to="/login" className="text-amber-400 underline">
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  const { groups, labels } = groupByDate(items, lang);
  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="notifications-page">
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Shadow</span>
          </Link>
          <h1 className="text-base font-bold uppercase tracking-[0.18em] md:text-lg">
            {tShadow("notificaciones", lang)}
          </h1>
          {unread > 0 ? (
            <button
              type="button"
              onClick={markAllRead}
              data-testid="notif-mark-all-read"
              className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-200 hover:bg-cyan-400/[0.12]"
            >
              <Check className="h-3 w-3" />
              {tShadow("marcarLeidas", lang)}
            </button>
          ) : (
            <span className="w-20" />
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 md:px-8">
        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-white/45">
            …
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="notif-empty"
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-16 text-center text-white/50"
          >
            {tShadow("sinNotif", lang)}
          </div>
        ) : (
          ["today", "yesterday", "earlier"].map(
            (k) =>
              groups[k].length > 0 && (
                <section key={k} className="mb-8">
                  <h2 className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">
                    {labels[k]}
                  </h2>
                  <ul className="space-y-2">
                    {groups[k].map((n) => (
                      <li
                        key={n.id}
                        data-testid={`notif-row-${n.id}`}
                        className={`flex items-start gap-3 rounded-2xl border p-4 transition-colors ${
                          n.read
                            ? "border-white/5 bg-white/[0.02]"
                            : "border-amber-400/25 bg-amber-400/[0.05]"
                        }`}
                      >
                        {n.actor ? (
                          <UserAvatar user={n.actor} size="md" linkToProfile />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-lg">
                            🏅
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-relaxed text-white/85">
                            {n.actor && (
                              <Link
                                to={`/shadow/perfil/${n.actor.username || n.actor.id}`}
                                className="font-bold text-white hover:underline"
                              >
                                {n.actor.name || n.actor.username || "—"}
                              </Link>
                            )}{" "}
                            {tShadow(COPY[n.type] || "notificaciones", lang)}
                          </p>
                          <span className="text-[10px] uppercase tracking-[0.15em] text-white/35">
                            {n.created_at?.slice(0, 16).replace("T", " ")}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )
          )
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
